import {createSelector} from 'reselect';

import {App, AppStatusStates} from '../../types/commons';
import {KeyedAction} from '../Store';
import {RootState} from '../State';
import {objectsFromIds} from '../../utils/byIds';

// Reducer
export interface AppsById {
    [n: string]: App;
}

export interface AppStatus {
    [id: string]: AppStatusStates;
}

export type DirectoryState = Readonly<{
    byId: AppsById;
    ids: string[];
    statusById: AppStatus;
}>;

export const DirectoryInitialState: DirectoryState = {
    byId: {},
    ids: [],
    statusById: {}
};

/**
 * Perform a bulk update of the directory apps in the store - for example, after login.
 */
export class UpdateDirectory extends KeyedAction<RootState, 'directory'> {
    public readonly type: string = '@@DIRECTORY/UPDATE_DIRECTORY';

    constructor(private readonly apps: App[]) {
        super('directory');
    }

    public reduce(state: DirectoryState): DirectoryState {
        const byId = {};
        const statusById = {};
        const ids: string[] = [];
        this.apps.forEach((app) => {
            byId[app.id] = app;
            ids.push(app.id);
            if (state.statusById[app.id]) {
                statusById[app.id] = state.statusById[app.id];
            }
        });
        return {byId, ids, statusById};
    }
}

/**
 * Perform an update for the launch of a directory app
 */
export class LaunchApp extends KeyedAction<RootState, 'directory'> {
    public readonly type: string = '@@DIRECTORY/LAUNCH_APP';

    constructor(public readonly app: App) {
        super('directory');
    }

    public reduce(state: DirectoryState): DirectoryState {
        const statusById = {...state.statusById, [this.app.id]: AppStatusStates.Loading};
        return {...state, statusById};
    }
}

/**
 * Perform an update of the status of a directory app
 */
export class UpdateAppStatus extends KeyedAction<RootState, 'directory'> {
    public readonly type: string = '@@DIRECTORY/UPDATE_APP_STATUS';

    constructor(private readonly app: App, private readonly status: AppStatusStates) {
        super('directory');
    }

    public reduce(state: DirectoryState): DirectoryState {
        const statusById = {...state.statusById};
        if (this.status === AppStatusStates.Closed) {
            delete statusById[this.app.id];
        } else {
            statusById[this.app.id] = this.status;
        }
        return {...state, statusById};
    }
}

export const getAppsById = (state: RootState) => state.directory.byId;
export const getAppById = (state: RootState, id: string): App => getAppsById(state)[id];
export const getAppsIds = (state: RootState) => state.directory.ids;
export const getPinnedApps = (state: RootState) => state.settings.pinnedAppIds.filter((id) => id in state.directory.byId).map((id) => getAppById(state, id));
export const getAppStatuses = (state: RootState): AppStatus => state.directory.statusById;

export const getApps = createSelector(
    [getAppsById, getAppsIds],
    (byId, ids) => ids.map((id) => byId[id])
);

export const getAppsDirectoryAppList = createSelector(
    [getAppsById, getAppsIds],
    objectsFromIds
);
